import React, { useState, useEffect } from 'react';
import './Perfil.css';
import api from '../services/api';
import { authService } from '../services/auth';
import { toast } from 'react-toastify';
import SubscriptionDashboard from './SubscriptionDashboard';

const Perfil = () => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editMode, setEditMode] = useState(false);
    const [showSubscription, setShowSubscription] = useState(false);
    const [activeTab, setActiveTab] = useState('personal'); // personal, contact, professional, preferences, security
    const [showChangePassword, setShowChangePassword] = useState(false);

    const [formData, setFormData] = useState({
        // Datos básicos
        name: '',
        // Datos de contacto
        phone: '',
        address: '',
        city: '',
        country: '',    
        zipCode: '',
        // Datos profesionales
        companyName: '',
        taxId: '',
        licenseNumber: '',
        // Perfil
        bio: '',
        website: '',
        profileImage: '',
        // Preferencias
        language: 'es',
        timezone: 'America/Bogota',
        notifications: {
            email: true,
            sms: false
        }
    });

    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    const [previewImage, setPreviewImage] = useState(null);

    useEffect(() => {
        cargarPerfil();
    }, []);

    const cargarPerfil = async () => {
        try {
            setLoading(true);
            const response = await api.get('/users/profile');
            const usuario = response.data.data;
            setUser(usuario);
            setFormData({
                name: usuario.name || '',
                phone: usuario.phone || '',
                address: usuario.address || '',
                city: usuario.city || '',
                country: usuario.country || '',
                zipCode: usuario.zipCode || '',
                companyName: usuario.companyName || '',
                taxId: usuario.taxId || '',
                licenseNumber: usuario.licenseNumber || '',
                bio: usuario.bio || '',
                website: usuario.website || '',
                profileImage: usuario.profileImage || '',
                language: usuario.language || 'es',
                timezone: usuario.timezone || 'America/Bogota',
                notifications: usuario.notifications || { email: true, sms: false }
            });
            setLoading(false);
        } catch (error) {
            console.error('Error cargando perfil:', error);
            // Fallback a datos locales si falla
            const usuario = authService.getUser();
            if (usuario) {
                setUser(usuario);
                setFormData({
                    name: usuario.name || '',
                    language: 'es',
                    timezone: 'America/Bogota',
                    notifications: { email: true, sms: false }
                });
            }
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (name.includes('notifications')) {
            const notifType = name.split('.')[1];
            setFormData(prev => ({
                ...prev,
                notifications: {
                    ...prev.notifications,
                    [notifType]: checked
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            }));
        }
    };

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswordData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSaveProfile = async () => {
        try {
            setLoading(true);

            let profileImageUrl = formData.profileImage;

            // Primero subir imagen si existe
            if (previewImage) {
                const fileInput = document.querySelector('input[type="file"][name="profileImageInput"]');
                const file = fileInput?.files[0];

                if (file) {
                    const imageFormData = new FormData();
                    imageFormData.append('profileImage', file);

                    const imageResponse = await api.post('/users/profile-image/upload', imageFormData, {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    });

                    // Obtener la URL de la imagen subida
                    profileImageUrl = imageResponse.data.data.profileImage;
                    
                    // Actualizar formData con la URL de la imagen
                    setFormData(prev => ({
                        ...prev,
                        profileImage: profileImageUrl
                    }));

                    // Actualizar user con la nueva imagen
                    setUser(imageResponse.data.data);
                }
            }

            // Luego actualizar el perfil con todos los datos, incluyendo la URL de la imagen
            const updateData = {
                ...formData,
                profileImage: profileImageUrl
            };
            
            const response = await api.put('/users/profile', updateData);

            toast.success('Perfil actualizado correctamente');
            setEditMode(false);
            setPreviewImage(null);

            // Actualizar datos en localStorage
            const updatedUser = { ...response.data.data };
            authService.saveSession(authService.getToken(), updatedUser);
            setUser(updatedUser);
        } catch (error) {
            console.error('Error al actualizar perfil:', error);
            toast.error(error.response?.data?.error || 'Error al actualizar el perfil');
        } finally {
            setLoading(false);
        }
    };

    const handleChangePassword = async () => {
        if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
            toast.error('Todos los campos son requeridos');
            return;
        }

        if (passwordData.newPassword !== passwordData.confirmPassword) {
            toast.error('Las contraseñas no coinciden');
            return;
        }

        try {
            setLoading(true);
            await api.post('/users/change-password', {
                currentPassword: passwordData.currentPassword,
                newPassword: passwordData.newPassword
            });

            toast.success('Contraseña actualizada correctamente');
            setShowChangePassword(false);
            setPasswordData({
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            });
        } catch (error) {
            console.error('Error al cambiar contraseña:', error);
            toast.error(error.response?.data?.error || 'Error al cambiar la contraseña');
        } finally {
            setLoading(false);
        }
    };

    const handleImageSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validar tipo de archivo
        if (!file.type.startsWith('image/')) {
            toast.error('Solo se permiten imágenes');
            return;
        }

        // Validar tamaño (3MB máximo)
        if (file.size > 3 * 1024 * 1024) {
            toast.error('La imagen no debe superar 3MB');
            return;
        }

        // Crear preview
        const reader = new FileReader();
        reader.onload = (e) => {
            setPreviewImage(e.target.result);
        };
        reader.readAsDataURL(file);
    };

    const handleDeleteImage = async () => {
        if (!window.confirm('¿Eliminar la foto de perfil?')) return;

        try {
            setLoading(true);
            const response = await api.delete('/users/profile-image');
            toast.success('Foto de perfil eliminada');
            setUser(response.data.data);
            setPreviewImage(null);
        } catch (error) {
            console.error('Error al eliminar imagen:', error);
            toast.error('Error al eliminar la foto');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setEditMode(false);
        setShowChangePassword(false);
        setPasswordData({
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
        });
        setPreviewImage(null);
        cargarPerfil();
    };

    if (loading) {
        return <div className="perfil-loading">Cargando perfil...</div>;
    }

    if (showSubscription) {
        return (
            <div className="perfil-container">
                <button
                    className="btn-volver"
                    onClick={() => setShowSubscription(false)}
                >
                    ← Volver al Perfil
                </button>
                <SubscriptionDashboard />
            </div>
        );
    }

    return (
        <div className="perfil-container">
            <div className="perfil-card">
                <div className="perfil-header">
                    <div className="perfil-avatar">
                        {user?.profileImage ? (
                            <img src={user.profileImage} alt={user.name} />
                        ) : (
                            '👤'
                        )}
                    </div>
                    <h1>Mi Perfil</h1>
                </div>

                {!editMode && !showChangePassword ? (
                    <>
                        {/* VISTA DE SOLO LECTURA */}
                        <div className="perfil-tabs">
                            <button
                                className={`tab-btn ${activeTab === 'personal' ? 'active' : ''}`}
                                onClick={() => setActiveTab('personal')}
                            >
                                ℹ️ Datos Personales
                            </button>
                            <button
                                className={`tab-btn ${activeTab === 'contact' ? 'active' : ''}`}
                                onClick={() => setActiveTab('contact')}
                            >
                                📞 Contacto
                            </button>
                            <button
                                className={`tab-btn ${activeTab === 'professional' ? 'active' : ''}`}
                                onClick={() => setActiveTab('professional')}
                            >
                                💼 Profesional
                            </button>
                            <button
                                className={`tab-btn ${activeTab === 'preferences' ? 'active' : ''}`}
                                onClick={() => setActiveTab('preferences')}
                            >
                                ⚙️ Preferencias
                            </button>
                        </div>

                        <div className="perfil-info">
                            {activeTab === 'personal' && (
                                <div className="info-section">
                                    <h3>Datos Personales</h3>
                                    <div className="info-grid">
                                        <div className="info-item">
                                            <label>Nombre</label>
                                            <p>{user?.name}</p>
                                        </div>
                                        <div className="info-item">
                                            <label>Email</label>
                                            <p>{user?.email}</p>
                                        </div>
                                        <div className="info-item">
                                            <label>Estado</label>
                                            <p className="status-verified">
                                                {user?.isVerified ? '✓ Verificado' : '⚠ Pendiente'}
                                            </p>
                                        </div>
                                        <div className="info-item">
                                            <label>Fecha de Registro</label>
                                            <p>{user?.createdAt ? new Date(user.createdAt).toLocaleDateString('es-ES') : 'N/A'}</p>
                                        </div>
                                        {user?.bio && (
                                            <div className="info-item full-width">
                                                <label>Biografía</label>
                                                <p>{user.bio}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {activeTab === 'contact' && (
                                <div className="info-section">
                                    <h3>Información de Contacto</h3>
                                    <div className="info-grid">
                                        <div className="info-item">
                                            <label>Teléfono</label>
                                            <p>{user?.phone || '—'}</p>
                                        </div>
                                        <div className="info-item">
                                            <label>Dirección</label>
                                            <p>{user?.address || '—'}</p>
                                        </div>
                                        <div className="info-item">
                                            <label>Ciudad</label>
                                            <p>{user?.city || '—'}</p>
                                        </div>
                                        <div className="info-item">
                                            <label>País</label>
                                            <p>{user?.country || '—'}</p>
                                        </div>
                                        <div className="info-item">
                                            <label>Código Postal</label>
                                            <p>{user?.zipCode || '—'}</p>
                                        </div>
                                        {user?.website && (
                                            <div className="info-item">
                                                <label>Sitio Web</label>
                                                <p><a href={user.website} target="_blank" rel="noopener noreferrer">{user.website}</a></p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {activeTab === 'professional' && (
                                <div className="info-section">
                                    <h3>Información Profesional</h3>
                                    <div className="info-grid">
                                        <div className="info-item">
                                            <label>Empresa</label>
                                            <p>{user?.companyName || '—'}</p>
                                        </div>
                                        <div className="info-item">
                                            <label>RUC/NIF</label>
                                            <p>{user?.taxId || '—'}</p>
                                        </div>
                                        <div className="info-item">
                                            <label>Licencia Profesional</label>
                                            <p>{user?.licenseNumber || '—'}</p>
                                        </div>
                                        <div className="info-item">
                                            <label>Rol</label>
                                            <p className="role-badge">{user?.role || 'Usuario'}</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'preferences' && (
                                <div className="info-section">
                                    <h3>Preferencias</h3>
                                    <div className="info-grid">
                                        <div className="info-item">
                                            <label>Idioma</label>
                                            <p>{user?.language === 'es' ? 'Español' : user?.language}</p>
                                        </div>
                                        <div className="info-item">
                                            <label>Zona Horaria</label>
                                            <p>{user?.timezone || 'America/Bogota'}</p>
                                        </div>
                                        <div className="info-item">
                                            <label>Notificaciones por Email</label>
                                            <p>{user?.notifications?.email ? '✓ Activadas' : '✗ Desactivadas'}</p>
                                        </div>
                                        <div className="info-item">
                                            <label>Notificaciones por SMS</label>
                                            <p>{user?.notifications?.sms ? '✓ Activadas' : '✗ Desactivadas'}</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="perfil-actions">
                            <button
                                className="btn btn-primary"
                                onClick={() => setEditMode(true)}
                            >
                                ✏️ Editar Perfil
                            </button>
                            <button
                                className="btn btn-secondary"
                                onClick={() => setShowChangePassword(true)}
                            >
                                🔐 Cambiar Contraseña
                            </button>
                            <button
                                className="btn btn-info"
                                onClick={() => setShowSubscription(true)}
                            >
                                💳 Ver Suscripción
                            </button>
                        </div>
                    </>
                ) : showChangePassword ? (
                    <>
                        {/* CAMBIAR CONTRASEÑA */}
                        <div className="perfil-form">
                            <div className="form-section">
                                <h3>Cambiar Contraseña</h3>
                                <div className="form-group">
                                    <label htmlFor="currentPassword">Contraseña Actual</label>
                                    <input
                                        type="password"
                                        id="currentPassword"
                                        name="currentPassword"
                                        value={passwordData.currentPassword}
                                        onChange={handlePasswordChange}
                                        placeholder="Tu contraseña actual"
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="newPassword">Nueva Contraseña</label>
                                    <input
                                        type="password"
                                        id="newPassword"
                                        name="newPassword"
                                        value={passwordData.newPassword}
                                        onChange={handlePasswordChange}
                                        placeholder="Nueva contraseña"
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="confirmPassword">Confirmar Contraseña</label>
                                    <input
                                        type="password"
                                        id="confirmPassword"
                                        name="confirmPassword"
                                        value={passwordData.confirmPassword}
                                        onChange={handlePasswordChange}
                                        placeholder="Confirma tu nueva contraseña"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="perfil-form-actions">
                            <button
                                className="btn btn-success"
                                onClick={handleChangePassword}
                                disabled={loading}
                            >
                                💾 Cambiar Contraseña
                            </button>
                            <button
                                className="btn btn-secondary"
                                onClick={handleCancel}
                            >
                                ❌ Cancelar
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        {/* MODO EDICIÓN */}
                        <div className="perfil-tabs">
                            <button
                                className={`tab-btn ${activeTab === 'personal' ? 'active' : ''}`}
                                onClick={() => setActiveTab('personal')}
                            >
                                ℹ️ Datos Personales
                            </button>
                            <button
                                className={`tab-btn ${activeTab === 'contact' ? 'active' : ''}`}
                                onClick={() => setActiveTab('contact')}
                            >
                                📞 Contacto
                            </button>
                            <button
                                className={`tab-btn ${activeTab === 'professional' ? 'active' : ''}`}
                                onClick={() => setActiveTab('professional')}
                            >
                                💼 Profesional
                            </button>
                            <button
                                className={`tab-btn ${activeTab === 'preferences' ? 'active' : ''}`}
                                onClick={() => setActiveTab('preferences')}
                            >
                                ⚙️ Preferencias
                            </button>
                        </div>

                        <div className="perfil-form">
                            {activeTab === 'personal' && (
                                <div className="form-section">
                                    <h3>Datos Personales</h3>
                                    <div className="form-group">
                                        <label htmlFor="name">Nombre Completo *</label>
                                        <input
                                            type="text"
                                            id="name"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            placeholder="Tu nombre completo"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="bio">Biografía</label>
                                        <textarea
                                            id="bio"
                                            name="bio"
                                            value={formData.bio}
                                            onChange={handleInputChange}
                                            placeholder="Cuéntanos sobre ti"
                                            rows="4"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Foto de Perfil</label>
                                        <div className="profile-image-upload">
                                            {previewImage || user?.profileImage ? (
                                                <img
                                                    src={previewImage || user?.profileImage}
                                                    alt="Preview"
                                                    className="profile-preview"
                                                />
                                            ) : (
                                                <div className="profile-placeholder">📷</div>
                                            )}

                                            <div className="upload-buttons">
                                                <input
                                                    type="file"
                                                    name="profileImageInput"
                                                    accept="image/*"
                                                    onChange={handleImageSelect}
                                                    id="profileImageInput"
                                                    style={{ display: 'none' }}
                                                />
                                                <button
                                                    type="button"
                                                    className="btn btn-secondary"
                                                    onClick={() => document.getElementById('profileImageInput').click()}
                                                >
                                                    📁 Seleccionar Imagen
                                                </button>
                                                {!previewImage && user?.profileImage && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-danger"
                                                        onClick={handleDeleteImage}
                                                    >
                                                        🗑️ Eliminar Foto
                                                    </button>
                                                )}
                                            </div>
                                            <small>Máximo 3MB. Se ajustará a 500x500px</small>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'contact' && (
                                <div className="form-section">
                                    <h3>Información de Contacto</h3>
                                    <div className="form-group">
                                        <label htmlFor="phone">Teléfono</label>
                                        <input
                                            type="tel"
                                            id="phone"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                            placeholder="+57 300 123 4567"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="address">Dirección</label>
                                        <input
                                            type="text"
                                            id="address"
                                            name="address"
                                            value={formData.address}
                                            onChange={handleInputChange}
                                            placeholder="Calle 123 #45-67"
                                        />
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="city">Ciudad</label>
                                            <input
                                                type="text"
                                                id="city"
                                                name="city"
                                                value={formData.city}
                                                onChange={handleInputChange}
                                                placeholder="Bogotá"
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="country">País</label>
                                            <input
                                                type="text"
                                                id="country"
                                                name="country"
                                                value={formData.country}
                                                onChange={handleInputChange}
                                                placeholder="Colombia"
                                            />
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="zipCode">Código Postal</label>
                                        <input
                                            type="text"
                                            id="zipCode"
                                            name="zipCode"
                                            value={formData.zipCode}
                                            onChange={handleInputChange}
                                            placeholder="110111"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="website">Sitio Web</label>
                                        <input
                                            type="url"
                                            id="website"
                                            name="website"
                                            value={formData.website}
                                            onChange={handleInputChange}
                                            placeholder="https://ejemplo.com"
                                        />
                                    </div>
                                </div>
                            )}

                            {activeTab === 'professional' && (
                                <div className="form-section">
                                    <h3>Información Profesional</h3>
                                    <div className="form-group">
                                        <label htmlFor="companyName">Nombre de la Empresa</label>
                                        <input
                                            type="text"
                                            id="companyName"
                                            name="companyName"
                                            value={formData.companyName}
                                            onChange={handleInputChange}
                                            placeholder="Mi Inmobiliaria S.A.S."
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="taxId">RUC / NIF</label>
                                        <input
                                            type="text"
                                            id="taxId"
                                            name="taxId"
                                            value={formData.taxId}
                                            onChange={handleInputChange}
                                            placeholder="123456789-0"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="licenseNumber">Licencia Profesional</label>
                                        <input
                                            type="text"
                                            id="licenseNumber"
                                            name="licenseNumber"
                                            value={formData.licenseNumber}
                                            onChange={handleInputChange}
                                            placeholder="LIC-2025-123456"
                                        />
                                    </div>
                                </div>
                            )}

                            {activeTab === 'preferences' && (
                                <div className="form-section">
                                    <h3>Preferencias</h3>
                                    <div className="form-group">
                                        <label htmlFor="language">Idioma</label>
                                        <select
                                            id="language"
                                            name="language"
                                            value={formData.language}
                                            onChange={handleInputChange}
                                        >
                                            <option value="es">Español</option>
                                            {/* <option value="en">English</option>
                                            <option value="pt">Português</option> */}
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="timezone">Zona Horaria</label>
                                        <select
                                            id="timezone"
                                            name="timezone"
                                            value={formData.timezone}
                                            onChange={handleInputChange}
                                        >
                                            <option value="America/Bogota">America/Bogota (Colombia)</option>
                                            <option value="America/Mexico_City">America/Mexico_City (México)</option>
                                            <option value="America/Argentina/Buenos_Aires">America/Argentina/Buenos Aires</option>
                                            <option value="America/Sao_Paulo">America/Sao Paulo (Brasil)</option>
                                            <option value="Europe/Madrid">Europe/Madrid (España)</option>
                                            <option value="UTC">UTC</option>
                                        </select>
                                    </div>

                                    <div className="form-group checkbox">
                                        <label>
                                            <input
                                                type="checkbox"
                                                name="notifications.email"
                                                checked={formData.notifications.email}
                                                onChange={handleInputChange}
                                            />
                                            Recibir notificaciones por Email
                                        </label>
                                    </div>

                                    <div className="form-group checkbox">
                                        <label>
                                            <input
                                                type="checkbox"
                                                name="notifications.sms"
                                                checked={formData.notifications.sms}
                                                onChange={handleInputChange}
                                            />
                                            Recibir notificaciones por SMS
                                        </label>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="perfil-form-actions">
                            <button
                                className="btn btn-success"
                                onClick={handleSaveProfile}
                                disabled={loading}
                            >
                                💾 Guardar Cambios
                            </button>
                            <button
                                className="btn btn-secondary"
                                onClick={handleCancel}
                            >
                                ❌ Cancelar
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default Perfil;